<script>
import { useAlert } from 'dashboard/composables';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import countries from 'shared/constants/countries.js';
import { isPhoneNumberValid } from 'shared/helpers/Validators';
import parsePhoneNumber from 'libphonenumber-js';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Avatar from 'next/avatar/Avatar.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import CompanyAPI from 'dashboard/api/companies';

export default {
  components: {
    NextButton,
    Avatar,
    ComboBox,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    inProgress: {
      type: Boolean,
      default: false,
    },
    onSubmit: {
      type: Function,
      default: () => {},
    },
  },
  emits: ['cancel', 'success'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      countries: countries,
      email: '',
      name: '',
      phoneNumber: '',
      activeDialCode: '',
      avatarFile: null,
      avatarUrl: '',
      country: {
        id: '',
        name: '',
      },
      city: '',
      extraFields: {},
      companySuggestions: [],
      showCompanySuggestions: false,
      companySearchTimer: null,
      selectedCompanyId: null,
      linkedCompanyName: '',
    };
  },
  validations: {
    name: {
      required,
    },
    email: {
      email,
    },
    phoneNumber: {},
  },
  computed: {
    contactFieldDefs() {
      return this.$store.getters['contactFieldDefinitions/getEnabled'] || [];
    },
    parsePhoneNumber() {
      return parsePhoneNumber(this.phoneNumber);
    },
    isPhoneNumberNotValid() {
      if (this.phoneNumber !== '') {
        return (
          !isPhoneNumberValid(this.phoneNumber, this.activeDialCode) ||
          (this.phoneNumber !== '' ? this.activeDialCode === '' : false)
        );
      }
      return false;
    },
    phoneNumberError() {
      if (this.activeDialCode === '') {
        return this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.DIAL_CODE_ERROR');
      }
      if (!isPhoneNumberValid(this.phoneNumber, this.activeDialCode)) {
        return this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.ERROR');
      }
      return '';
    },
    setPhoneNumber() {
      if (this.parsePhoneNumber && this.parsePhoneNumber.countryCallingCode) {
        return this.phoneNumber;
      }
      if (this.phoneNumber === '' && this.activeDialCode !== '') {
        return '';
      }
      return this.activeDialCode
        ? `${this.activeDialCode}${this.phoneNumber}`
        : '';
    },
  },
  watch: {
    contact() {
      this.setContactObject();
    },
  },
  mounted() {
    this.$store.dispatch('contactFieldDefinitions/fetchAll');
    this.setContactObject();
    this.setDialCode();
  },
  methods: {
    onCancel() {
      this.$emit('cancel');
    },
    onSuccess() {
      this.$emit('success');
    },
    countryNameWithCode({ name, id }) {
      if (!id) return name;
      if (!name && !id) return '';
      return `${name} (${id})`;
    },
    onCountryChange(value) {
      const selected = this.countries.find(c => c.id === value);
      this.country = selected
        ? { id: selected.id, name: selected.name }
        : { id: '', name: '' };
    },
    setDialCode() {
      if (
        this.phoneNumber !== '' &&
        this.parsePhoneNumber &&
        this.parsePhoneNumber.countryCallingCode
      ) {
        const dialCode = this.parsePhoneNumber.countryCallingCode;
        this.activeDialCode = `+${dialCode}`;
      }
    },
    setContactObject() {
      const {
        email: emailAddress,
        phone_number: phoneNumber,
        name,
        identifier,
      } = this.contact;
      const additionalAttributes = this.contact.additional_attributes || {};

      this.name = name || '';
      this.email = emailAddress || '';
      this.phoneNumber = phoneNumber || '';
      this.country = {
        id: additionalAttributes.country_code || '',
        name:
          additionalAttributes.country ||
          this.$t('CONTACT_FORM.FORM.COUNTRY.SELECT_COUNTRY'),
      };
      this.city = additionalAttributes.city || '';
      this.avatarUrl = this.contact.thumbnail || '';
      this.extraFields = { ...(this.contact.extra_fields || {}) };
    },
    getContactObject() {
      if (this.country === null) {
        this.country = { id: '', name: '' };
      }
      const contactObject = {
        id: this.contact.id,
        name: this.name,
        email: this.email,
        phone_number: this.setPhoneNumber,
        additional_attributes: {
          ...this.contact.additional_attributes,
          country_code: this.country.id,
          country:
            this.country.name ===
            this.$t('CONTACT_FORM.FORM.COUNTRY.SELECT_COUNTRY')
              ? ''
              : this.country.name,
          city: this.city,
        },
        extraFields: { ...this.extraFields },
      };
      if (this.selectedCompanyId) {
        contactObject.company_id = this.selectedCompanyId;
      }
      if (this.avatarFile) {
        contactObject.avatar = this.avatarFile;
        contactObject.isFormData = true;
      }
      return contactObject;
    },
    onCompanyDomainInput(fieldKey, value) {
      this.extraFields = { ...this.extraFields, [fieldKey]: value };
      this.selectedCompanyId = null;
      this.linkedCompanyName = '';
      clearTimeout(this.companySearchTimer);
      if (!value || value.length < 2) {
        this.companySuggestions = [];
        this.showCompanySuggestions = false;
        return;
      }
      this.companySearchTimer = setTimeout(async () => {
        try {
          const { data } = await CompanyAPI.search(value);
          this.companySuggestions = data.payload || [];
          this.showCompanySuggestions = true;
        } catch (error) {
          this.companySuggestions = [];
          this.showCompanySuggestions = false;
        }
      }, 300);
    },
    selectCompany(fieldKey, company) {
      this.extraFields = {
        ...this.extraFields,
        [fieldKey]: company.domain || company.name || '',
      };
      this.selectedCompanyId = company.id;
      this.linkedCompanyName = company.name;
      this.showCompanySuggestions = false;
    },
    hideCompanySuggestions() {
      setTimeout(() => {
        this.showCompanySuggestions = false;
      }, 150);
    },
    setPhoneCode(code) {
      if (this.phoneNumber !== '' && this.parsePhoneNumber) {
        const dialCode = this.parsePhoneNumber.countryCallingCode;
        if (dialCode === code) {
          return;
        }
        this.activeDialCode = `+${dialCode}`;
        const newPhoneNumber = this.phoneNumber.replace(
          `+${dialCode}`,
          `${code}`
        );
        this.phoneNumber = newPhoneNumber;
      } else {
        this.activeDialCode = code;
      }
    },
    async handleSubmit() {
      this.v$.$touch();
      if (this.v$.$invalid || this.isPhoneNumberNotValid) {
        return;
      }
      try {
        await this.onSubmit(this.getContactObject());
        this.onSuccess();
        useAlert(this.$t('CONTACT_FORM.SUCCESS_MESSAGE'));
      } catch (error) {
        if (error instanceof DuplicateContactException) {
          if (error.data.includes('email')) {
            useAlert(this.$t('CONTACT_FORM.FORM.EMAIL_ADDRESS.DUPLICATE'));
          } else if (error.data.includes('phone_number')) {
            useAlert(this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.DUPLICATE'));
          }
        } else if (error instanceof ExceptionWithMessage) {
          useAlert(error.data);
        } else {
          useAlert(this.$t('CONTACT_FORM.ERROR_MESSAGE'));
        }
      }
    },
    handleImageUpload({ file, url }) {
      this.avatarFile = file;
      this.avatarUrl = url;
    },
    async handleAvatarDelete() {
      try {
        if (this.contact && this.contact.id) {
          await this.$store.dispatch('contacts/deleteAvatar', this.contact.id);
          useAlert(this.$t('CONTACT_FORM.DELETE_AVATAR.API.SUCCESS_MESSAGE'));
        }
        this.avatarFile = null;
        this.avatarUrl = '';
        this.activeDialCode = '';
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t('CONTACT_FORM.DELETE_AVATAR.API.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <form
    class="w-full px-8 pt-6 pb-8 contact--form"
    @submit.prevent="handleSubmit"
  >
    <div class="flex flex-col mb-4 items-start gap-1 w-full">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ $t('CONTACT_FORM.FORM.AVATAR.LABEL') }}
      </label>
      <Avatar
        :src="avatarUrl"
        :size="72"
        :name="contact.name"
        allow-upload
        rounded-full
        @upload="handleImageUpload"
        @delete="handleAvatarDelete"
      />
    </div>
    <div>
      <div class="w-full">
        <label :class="{ error: v$.name.$error }">
          {{ $t('CONTACT_FORM.FORM.NAME.LABEL') }}
          <input
            v-model="name"
            type="text"
            :placeholder="$t('CONTACT_FORM.FORM.NAME.PLACEHOLDER')"
            @input="v$.name.$touch"
          />
        </label>

        <label :class="{ error: v$.email.$error }">
          {{ $t('CONTACT_FORM.FORM.EMAIL_ADDRESS.LABEL') }}
          <input
            v-model="email"
            type="text"
            :placeholder="$t('CONTACT_FORM.FORM.EMAIL_ADDRESS.PLACEHOLDER')"
            @input="v$.email.$touch"
          />
          <span v-if="v$.email.$error" class="message">
            {{ $t('CONTACT_FORM.FORM.EMAIL_ADDRESS.ERROR') }}
          </span>
        </label>
      </div>
    </div>
    <div>
      <div class="w-full">
        <label :class="{ error: isPhoneNumberNotValid }">
          {{ $t('CONTACT_FORM.FORM.PHONE_NUMBER.LABEL') }}
          <woot-phone-input
            v-model="phoneNumber"
            :value="phoneNumber"
            :error="isPhoneNumberNotValid"
            :placeholder="$t('CONTACT_FORM.FORM.PHONE_NUMBER.PLACEHOLDER')"
            @blur="v$.phoneNumber.$touch"
            @set-code="setPhoneCode"
          />
          <span v-if="isPhoneNumberNotValid" class="message">
            {{ phoneNumberError }}
          </span>
        </label>
        <div
          v-if="isPhoneNumberNotValid || !phoneNumber"
          class="relative mx-0 mt-0 mb-2.5 p-2 rounded-md text-sm border border-solid border-n-amber-5 text-n-amber-12 bg-n-amber-3"
        >
          {{ $t('CONTACT_FORM.FORM.PHONE_NUMBER.HELP') }}
        </div>
      </div>
    </div>
    <div class="w-full mb-4">
      <label>
        {{ $t('CONTACT_FORM.FORM.COUNTRY.LABEL') }}
      </label>
      <ComboBox
        :model-value="country.id"
        :options="
          countries.map(c => ({
            value: c.id,
            label: countryNameWithCode(c),
          }))
        "
        class="[&>div>button]:!bg-n-alpha-black2"
        :placeholder="$t('CONTACT_FORM.FORM.COUNTRY.PLACEHOLDER')"
        :search-placeholder="$t('CONTACT_FORM.FORM.COUNTRY.SELECT_PLACEHOLDER')"
        @update:model-value="onCountryChange"
      />
    </div>
    <woot-input
      v-model="city"
      class="w-full"
      :label="$t('CONTACT_FORM.FORM.CITY.LABEL')"
      :placeholder="$t('CONTACT_FORM.FORM.CITY.PLACEHOLDER')"
    />
    <!-- Contact Fields (from Settings > Contact Fields) -->
    <template v-if="contactFieldDefs.length">
      <div class="w-full mb-4">
        <label class="mb-1 text-sm font-medium text-n-slate-12 block">
          {{ $t('CONTACT_FORM.FORM.CUSTOM_FIELDS.LABEL', 'Contact Fields') }}
        </label>
        <div v-for="field in contactFieldDefs" :key="field.id" class="mb-3">
          <!-- Checkbox -->
          <template v-if="field.field_type === 'checkbox'">
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                :checked="!!extraFields[field.field_key]"
                class="size-4 rounded accent-woot-500"
                @change="extraFields = { ...extraFields, [field.field_key]: $event.target.checked }"
              />
              <span class="text-sm text-n-slate-12">{{ field.label }}</span>
            </label>
          </template>
          <!-- List / dropdown -->
          <template v-else-if="field.field_type === 'list'">
            <label class="text-sm font-medium text-n-slate-12 block mb-1">{{ field.label }}</label>
            <select
              :value="extraFields[field.field_key] || ''"
              class="w-full h-9 px-2 text-sm rounded-md border border-solid border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500"
              @change="extraFields = { ...extraFields, [field.field_key]: $event.target.value }"
            >
              <option value="">— Select —</option>
              <option v-for="opt in (field.options || [])" :key="opt" :value="opt">{{ opt }}</option>
            </select>
          </template>
          <!-- Date -->
          <template v-else-if="field.field_type === 'date'">
            <label class="text-sm font-medium text-n-slate-12 block mb-1">{{ field.label }}</label>
            <input
              type="date"
              :value="extraFields[field.field_key] || ''"
              class="w-full h-9 px-2 text-sm rounded-md border border-solid border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500"
              @input="extraFields = { ...extraFields, [field.field_key]: $event.target.value }"
            />
          </template>
          <!-- Company domain autocomplete: search companies and link the contact -->
          <template v-else-if="field.field_key === 'company_domain'">
            <label class="text-sm font-medium text-n-slate-12 block mb-1">{{ field.label }}</label>
            <div class="relative">
              <input
                type="text"
                :value="extraFields[field.field_key] || ''"
                :placeholder="field.label"
                class="w-full h-9 px-2 text-sm rounded-md border border-solid border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500"
                @input="onCompanyDomainInput(field.field_key, $event.target.value)"
                @blur="hideCompanySuggestions"
              />
              <div
                v-if="showCompanySuggestions && companySuggestions.length"
                class="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto rounded-md border border-solid border-n-weak bg-n-solid-1 shadow-lg"
              >
                <button
                  v-for="company in companySuggestions"
                  :key="company.id"
                  type="button"
                  class="w-full px-3 py-2 text-left text-sm hover:bg-n-alpha-black1 flex flex-col"
                  @mousedown.prevent="selectCompany(field.field_key, company)"
                >
                  <span class="text-n-slate-12 font-medium">{{ company.name }}</span>
                  <span class="text-n-slate-11 text-xs">{{ company.domain }}</span>
                </button>
              </div>
            </div>
            <span v-if="linkedCompanyName" class="text-xs text-n-teal-11 mt-1 block">
              {{ $t('CONTACT_FORM.FORM.COMPANY_LINK_HINT', 'Will be linked to company:') }} {{ linkedCompanyName }}
            </span>
          </template>
          <!-- Text / Number / Link -->
          <template v-else>
            <label class="text-sm font-medium text-n-slate-12 block mb-1">{{ field.label }}</label>
            <input
              :type="field.field_type === 'number' ? 'number' : field.field_type === 'link' ? 'url' : 'text'"
              :value="extraFields[field.field_key] || ''"
              :placeholder="field.label"
              class="w-full h-9 px-2 text-sm rounded-md border border-solid border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500"
              @input="extraFields = { ...extraFields, [field.field_key]: $event.target.value }"
            />
          </template>
        </div>
      </div>
    </template>

    <div class="flex flex-row justify-start w-full gap-2 px-0 py-2">
      <NextButton
        type="submit"
        :label="$t('CONTACT_FORM.FORM.SUBMIT')"
        :is-loading="inProgress"
      />
      <NextButton
        faded
        slate
        type="reset"
        :label="$t('CONTACT_FORM.FORM.CANCEL')"
        @click.prevent="onCancel"
      />
    </div>
  </form>
</template>
